import { skipToken, useQuery } from '@tanstack/react-query';
import type { z } from 'zod';
import { INIS_NAME } from '@/consts/system-name';
import { zodRemoveNullable } from '@/lib/zod-helper';
import { inisApi } from '@/services';
import type { ApiResponse } from '@/types/api-types';
import { encounterTypeSchema } from '@/types/encounter-type-schema';
import type { QueryOptions } from '@/types/tanstack-query-options';
import type { paths } from '~/generated/inis/api-schema';
import { queryEncounterCardResSchema } from '~/generated/inis/zod-schema';

const PATH_VAR = '{encounterId}';
const API_PATH = `/v1/encounter/queryEncounter/${PATH_VAR}` as const;
const QUERY_KEY = '/v1/encounter/queryEncounter' as const;

type APIResponse = ApiResponse<paths, typeof API_PATH>;

const encounterCardSchema = zodRemoveNullable(queryEncounterCardResSchema, {
  includeMask: {
    id: true,
    visitStartDatetime: true,
  },
}).extend({
  encounterType: encounterTypeSchema,
});

export type QueryEncounterCardData = z.infer<typeof encounterCardSchema>;

const queryFn = async (encounterId: string, signal: AbortSignal) => {
  const { data: response } = await inisApi.get<APIResponse>(
    API_PATH.replace(PATH_VAR, encounterId),
    { signal },
  );
  return encounterCardSchema.parse(response.data);
};

export const useQueryEncounterCard = (
  encounterId?: string,
  options?: QueryOptions<QueryEncounterCardData>,
) => {
  return useQuery({
    queryKey: [QUERY_KEY, INIS_NAME, encounterId],
    queryFn: encounterId
      ? ({ signal }) => queryFn(encounterId, signal)
      : skipToken,
    ...options,
  });
};
